import { useState, useEffect, useCallback } from 'react'
import { requirementsApi } from '@/lib/api/client'
import { useEntityCrudListener } from '@/hooks/useEntityListener'
import type {
  Requirement,
  RequirementNode,
  RequirementColumnSettings,
  RequirementsSummary,
  RequirementStatus,
  RequirementTheme,
} from '@ispot/shared'
import { DEFAULT_VISIBLE_REQUIREMENT_COLUMNS } from '@/components/requirements/constants'

// ============================================================================
// TREE BUILDER
// ============================================================================

function buildTree(flat: Requirement[]): RequirementNode[] {
  const nodeMap = new Map<string, RequirementNode>()
  const roots: RequirementNode[] = []

  // First pass: create nodes
  for (const item of flat) {
    nodeMap.set(item.id, { ...item, children: [] })
  }

  // Second pass: link children to parents
  for (const item of flat) {
    const node = nodeMap.get(item.id)!
    if (item.parentId && nodeMap.has(item.parentId)) {
      nodeMap.get(item.parentId)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  // Sort children by sortOrder at each level
  const sortChildren = (nodes: RequirementNode[]) => {
    nodes.sort((a, b) => a.sortOrder - b.sortOrder)
    for (const node of nodes) {
      sortChildren(node.children)
    }
  }
  sortChildren(roots)

  return roots
}

// ============================================================================
// STATE
// ============================================================================

interface RequirementsState {
  requirements: Requirement[]
  tree: RequirementNode[]
  themes: RequirementTheme[]
  columnSettings: RequirementColumnSettings | null
  summary: RequirementsSummary | null
  selectedIds: Set<string>
  expandedIds: Set<string>
  isLoading: boolean
  isGenerating: boolean
  error: string | null
}

export function useRequirements(accountId: string, projectId: string) {
  const [state, setState] = useState<RequirementsState>({
    requirements: [],
    tree: [],
    themes: [],
    columnSettings: null,
    summary: null,
    selectedIds: new Set(),
    expandedIds: new Set(),
    isLoading: false,
    isGenerating: false,
    error: null,
  })

  // ============================================================================
  // LOAD
  // ============================================================================

  const loadRequirements = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const [items, settings, summary, themesResponse] = await Promise.all([
        requirementsApi.list(accountId, projectId) as Promise<Requirement[]>,
        requirementsApi.getColumnSettings(accountId, projectId) as Promise<RequirementColumnSettings | null>,
        requirementsApi.getSummary(accountId, projectId) as Promise<RequirementsSummary>,
        requirementsApi.listThemes(accountId, projectId) as Promise<{ themes: RequirementTheme[] }>,
      ])
      const tree = buildTree(items)

      // Auto-expand root items on first load
      const expandedIds = new Set<string>()
      for (const item of items) {
        if (item.depth === 0) expandedIds.add(item.id)
      }

      setState(s => ({
        ...s,
        requirements: items,
        tree,
        themes: themesResponse.themes ?? [],
        columnSettings: settings ?? {
          id: '',
          projectId,
          accountId,
          visibleColumns: DEFAULT_VISIBLE_REQUIREMENT_COLUMNS as Record<string, boolean>,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        summary,
        expandedIds: s.requirements.length === 0 ? expandedIds : s.expandedIds,
        isLoading: false,
      }))
    } catch {
      setState(s => ({ ...s, isLoading: false, error: 'Failed to load requirements' }))
    }
  }, [accountId, projectId])

  useEffect(() => {
    if (accountId && projectId) {
      loadRequirements()
    }
  }, [accountId, projectId, loadRequirements])

  useEntityCrudListener('Requirement', useCallback(() => loadRequirements(), [loadRequirements]))

  // ============================================================================
  // CRUD
  // ============================================================================

  const createRequirement = useCallback(async (data: {
    name: string
    description?: string
    type: Requirement['type']
    parentId?: string
    priority?: Requirement['priority']
    status?: Requirement['status']
    assigneeId?: string
  }) => {
    const req = await requirementsApi.create(accountId, projectId, data) as Requirement
    setState(s => {
      const requirements = [...s.requirements, req]
      return { ...s, requirements, tree: buildTree(requirements) }
    })
    return req
  }, [accountId, projectId])

  const updateRequirement = useCallback(async (id: string, data: Partial<Requirement>) => {
    // Optimistic update
    setState(s => {
      const requirements = s.requirements.map(r => r.id === id ? { ...r, ...data } : r)
      return { ...s, requirements, tree: buildTree(requirements) }
    })
    try {
      const updated = await requirementsApi.update(accountId, projectId, id, data) as Requirement
      setState(s => {
        const requirements = s.requirements.map(r => r.id === id ? updated : r)
        return { ...s, requirements, tree: buildTree(requirements) }
      })
      return updated
    } catch {
      await loadRequirements()
      throw new Error('Failed to update requirement')
    }
  }, [accountId, projectId, loadRequirements])

  const deleteRequirement = useCallback(async (id: string) => {
    // Optimistic: remove item and all descendants
    const toRemove = new Set<string>()
    const collectDescendants = (parentId: string) => {
      toRemove.add(parentId)
      for (const r of state.requirements) {
        if (r.parentId === parentId) collectDescendants(r.id)
      }
    }
    collectDescendants(id)

    setState(s => {
      const requirements = s.requirements.filter(r => !toRemove.has(r.id))
      return { ...s, requirements, tree: buildTree(requirements) }
    })
    try {
      await requirementsApi.delete(accountId, projectId, id)
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, state.requirements, loadRequirements])

  const bulkUpdateStatus = useCallback(async (status: RequirementStatus) => {
    const ids = Array.from(state.selectedIds)
    if (ids.length === 0) return

    setState(s => {
      const requirements = s.requirements.map(r =>
        s.selectedIds.has(r.id) ? { ...r, status } : r,
      )
      return { ...s, requirements, tree: buildTree(requirements), selectedIds: new Set() }
    })
    try {
      await requirementsApi.bulkUpdateStatus(accountId, projectId, { ids, status })
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, state.selectedIds, loadRequirements])

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(state.selectedIds)
    if (ids.length === 0) return
    // Collect all descendants of selected items too
    const toRemove = new Set<string>()
    const collectDescendants = (parentId: string) => {
      toRemove.add(parentId)
      for (const r of state.requirements) {
        if (r.parentId === parentId) collectDescendants(r.id)
      }
    }
    for (const id of ids) collectDescendants(id)

    setState(s => {
      const requirements = s.requirements.filter(r => !toRemove.has(r.id))
      return { ...s, requirements, tree: buildTree(requirements), selectedIds: new Set() }
    })
    try {
      await requirementsApi.bulkDelete(accountId, projectId, { ids })
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, state.selectedIds, state.requirements, loadRequirements])

  const bulkUpdatePriority = useCallback(async (priority: string | null) => {
    const ids = Array.from(state.selectedIds)
    if (ids.length === 0) return
    setState(s => {
      const requirements = s.requirements.map(r =>
        s.selectedIds.has(r.id) ? { ...r, priority: priority as Requirement['priority'] } : r,
      )
      return { ...s, requirements, tree: buildTree(requirements), selectedIds: new Set() }
    })
    try {
      await requirementsApi.bulkUpdatePriority(accountId, projectId, { ids, priority })
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, state.selectedIds, loadRequirements])

  // ============================================================================
  // REORDER
  // ============================================================================

  const reorderRequirements = useCallback(async (
    parentId: string | null,
    orderedIds: string[],
  ) => {
    const items = orderedIds.map((id, i) => ({ id, sortOrder: i }))

    // Optimistic: update sortOrder in local state
    setState(s => {
      const sortMap = new Map(items.map(it => [it.id, it.sortOrder]))
      const requirements = s.requirements.map(r =>
        sortMap.has(r.id) ? { ...r, sortOrder: sortMap.get(r.id)! } : r,
      )
      return { ...s, requirements, tree: buildTree(requirements) }
    })

    try {
      await requirementsApi.reorder(accountId, projectId, { items })
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, loadRequirements])

  // ============================================================================
  // UI STATE
  // ============================================================================

  const toggleExpanded = useCallback((id: string) => {
    setState(s => {
      const expandedIds = new Set(s.expandedIds)
      if (expandedIds.has(id)) expandedIds.delete(id)
      else expandedIds.add(id)
      return { ...s, expandedIds }
    })
  }, [])

  const toggleSelected = useCallback((id: string) => {
    setState(s => {
      const selectedIds = new Set(s.selectedIds)
      if (selectedIds.has(id)) selectedIds.delete(id)
      else selectedIds.add(id)
      return { ...s, selectedIds }
    })
  }, [])

  const selectAll = useCallback(() => {
    setState(s => ({
      ...s,
      selectedIds: new Set(s.requirements.map(r => r.id)),
    }))
  }, [])

  const deselectAll = useCallback(() => {
    setState(s => ({ ...s, selectedIds: new Set() }))
  }, [])

  const expandAll = useCallback(() => {
    setState(s => ({
      ...s,
      expandedIds: new Set(s.requirements.filter(r => r.depth < 3).map(r => r.id)),
    }))
  }, [])

  const collapseAll = useCallback(() => {
    setState(s => ({ ...s, expandedIds: new Set() }))
  }, [])

  // ============================================================================
  // COLUMN SETTINGS
  // ============================================================================

  const updateColumnSettings = useCallback(async (visibleColumns: Record<string, boolean>) => {
    setState(s => ({
      ...s,
      columnSettings: s.columnSettings ? { ...s.columnSettings, visibleColumns } : null,
    }))
    const settings = await requirementsApi.upsertColumnSettings(accountId, projectId, { visibleColumns }) as RequirementColumnSettings
    setState(s => ({ ...s, columnSettings: settings }))
  }, [accountId, projectId])

  // ============================================================================
  // AI GENERATION
  // ============================================================================

  const generateFromResearch = useCallback(async (apiKey: string) => {
    setState(s => ({ ...s, isGenerating: true, error: null }))
    try {
      const result = await requirementsApi.generate(accountId, projectId, { apiKey })
      await loadRequirements()
      return result
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate requirements'
      setState(s => ({ ...s, error: msg }))
      throw err
    } finally {
      setState(s => ({ ...s, isGenerating: false }))
    }
  }, [accountId, projectId, loadRequirements])

  const generateChildren = useCallback(async (parentId: string, apiKey: string) => {
    setState(s => ({ ...s, isGenerating: true, error: null }))
    try {
      const result = await requirementsApi.generateChildren(accountId, projectId, { parentId, apiKey })
      await loadRequirements()
      // Auto-expand the parent to show new children
      setState(s => {
        const expandedIds = new Set(s.expandedIds)
        expandedIds.add(parentId)
        return { ...s, expandedIds }
      })
      return result
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate children'
      setState(s => ({ ...s, error: msg }))
    } finally {
      setState(s => ({ ...s, isGenerating: false }))
    }
  }, [accountId, projectId, loadRequirements])

  // ============================================================================
  // THEMES + PERSONA LINKS
  // ============================================================================

  const setRequirementThemes = useCallback(async (
    requirementId: string,
    themes: Array<{ themeId: string; isPrimary?: boolean }>,
  ) => {
    // Optimistic: update local themeIds/primaryThemeId.
    setState(s => {
      const primary = themes.find(t => t.isPrimary)?.themeId ?? null
      const requirements = s.requirements.map(r =>
        r.id === requirementId
          ? { ...r, themeIds: themes.map(t => t.themeId), primaryThemeId: primary }
          : r,
      )
      return { ...s, requirements, tree: buildTree(requirements) }
    })
    try {
      await requirementsApi.setThemeLinks(accountId, projectId, requirementId, themes)
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, loadRequirements])

  const setRequirementPersonas = useCallback(async (
    requirementId: string, personaSessionIds: string[],
  ) => {
    setState(s => {
      const requirements = s.requirements.map(r =>
        r.id === requirementId ? { ...r, personaIds: personaSessionIds } : r,
      )
      return { ...s, requirements, tree: buildTree(requirements) }
    })
    try {
      await requirementsApi.setPersonaLinks(accountId, projectId, requirementId, personaSessionIds)
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, loadRequirements])

  const setPrimaryPersona = useCallback(async (
    requirementId: string, personaSessionId: string | null,
  ) => {
    try {
      await requirementsApi.setPrimaryPersona(accountId, projectId, requirementId, personaSessionId)
      await loadRequirements()
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, loadRequirements])

  const createTheme = useCallback(async (data: {
    name: string; color?: string | null; description?: string | null
  }): Promise<RequirementTheme> => {
    const { theme } = await requirementsApi.createTheme(accountId, projectId, data) as { theme: RequirementTheme }
    setState(s => ({ ...s, themes: [...s.themes, theme] }))
    return theme
  }, [accountId, projectId])

  const updateTheme = useCallback(async (themeId: string, data: {
    name?: string; color?: string | null; description?: string | null
  }): Promise<RequirementTheme> => {
    const { theme } = await requirementsApi.updateTheme(accountId, projectId, themeId, data) as { theme: RequirementTheme }
    setState(s => ({ ...s, themes: s.themes.map(t => t.id === themeId ? theme : t) }))
    return theme
  }, [accountId, projectId])

  const deleteTheme = useCallback(async (themeId: string) => {
    setState(s => ({
      ...s,
      themes: s.themes.filter(t => t.id !== themeId),
      // Strip deleted theme from all requirements locally; server cascades too.
      requirements: s.requirements.map(r => ({
        ...r,
        themeIds: r.themeIds.filter(id => id !== themeId),
        primaryThemeId: r.primaryThemeId === themeId ? null : r.primaryThemeId,
      })),
    }))
    setState(s => ({ ...s, tree: buildTree(s.requirements) }))
    try {
      await requirementsApi.deleteTheme(accountId, projectId, themeId)
    } catch {
      await loadRequirements()
    }
  }, [accountId, projectId, loadRequirements])

  return {
    ...state,
    loadRequirements,
    createRequirement,
    updateRequirement,
    deleteRequirement,
    bulkUpdateStatus,
    bulkDelete,
    bulkUpdatePriority,
    reorderRequirements,
    generateFromResearch,
    generateChildren,
    toggleExpanded,
    toggleSelected,
    selectAll,
    deselectAll,
    expandAll,
    collapseAll,
    updateColumnSettings,
    setRequirementThemes,
    setRequirementPersonas,
    setPrimaryPersona,
    createTheme,
    updateTheme,
    deleteTheme,
  }
}
